import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { isTextarea, isCustomUrlProperty } from 'customer-data-objects/property/PropertyIdentifier';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { useProperty } from '../v2/hooks/useProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { ExpandingTextArea } from 'foundations-components/transitional/components/ExpandingTextArea';
import { usePropertyValueFormatting } from '../v2/hooks/usePropertyValueFormatting';
import { trackExpandableTextCollapse, trackExpandableTextExpand } from '../utils/usageTrackingUtils';
import { useObjectTypeId } from '../v2/hooks/useObjectTypeId';
import { usePropertyValidationRules } from '../v2/hooks/usePropertyValidationRules';
import { useIsFocused } from '../v2/hooks/useIsFocused';
import { useHasUrlValidationRule } from '../v2/hooks/useHasUrlValidationRule';
import PropertyFormattedTooltip from './PropertyFormattedTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_PROTOCOL = 'https://';
const PropertyInputExpandableText = props => {
  const isFocused = useIsFocused();
  const cursorPositionedRef = useRef(false);
  const {
    onChange,
    onFocus,
    onBlur,
    inputRef,
    defaultValue,
    formControlUse
  } = props;
  const property = useProperty();
  const objectTypeId = useObjectTypeId();
  const value = usePropertyValue();
  const {
    onChange: normalizeInputOnChange,
    onBlur: normalizeInputOnBlur,
    onKeyDown: normalizeInputOnKeyDown,
    normalizationRules
  } = usePropertyValueFormatting({
    onChange,
    onBlur
  });
  useComponentMountMetric('PropertyInputExpandableText');
  const validationRules = usePropertyValidationRules();
  const hasUrlValidationRule = useHasUrlValidationRule(validationRules.data);
  const isUrlProperty = isCustomUrlProperty(property) || hasUrlValidationRule;
  const handleChange = useCallback(event => {
    const newValue = event.target.value;

    // Handle URL protocol duplication when pasting
    if (isUrlProperty) {
      const currentValue = value || '';

      // Pasting a full URL when there's already content starting with https://
      if (currentValue.startsWith('https://') && newValue.match(/^https:\/\/https?:\/\//)) {
        const cleanedValue = newValue.replace(/^https:\/\//, '');
        return normalizeInputOnChange(Object.assign({}, event, {
          target: Object.assign({}, event.target, {
            value: cleanedValue
          })
        }));
      }
    }
    if (!isTextarea(property)) {
      if (event.target.value.includes('\n')) {
        const withoutNewlines = newValue.replace(/\n/g, '');
        if (withoutNewlines === value) {
          return; // no change, don't call handler
        }

        // prevent newlines if not a textarea, for why we're doing this see https://git.hubteam.com/HubSpot/CRM-Issues/issues/4221
        return normalizeInputOnChange(Object.assign({}, event, {
          target: Object.assign({}, event.target, {
            value: withoutNewlines
          })
        }));
      }
    }
    return normalizeInputOnChange(Object.assign({}, event, {
      target: Object.assign({}, event.target, {
        value: newValue
      })
    }));
  }, [normalizeInputOnChange, property, value, isUrlProperty]);
  const handleFocus = useCallback(event => {
    if (isUrlProperty && !value) {
      const newCursorPosition = DEFAULT_PROTOCOL.length;
      normalizeInputOnChange(Object.assign({}, event, {
        target: Object.assign({}, event.target, {
          value: DEFAULT_PROTOCOL
        })
      }));
      requestAnimationFrame(() => {
        if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      });
    }
    onFocus === null || onFocus === void 0 || onFocus(event);
  }, [isUrlProperty, normalizeInputOnChange, onFocus, value, inputRef]);
  useLayoutEffect(() => {
    if (isFocused && inputRef !== null && inputRef !== void 0 && inputRef.current && value && !cursorPositionedRef.current && formControlUse === 'table-cell') {
      const valueLength = value.length;
      inputRef.current.setSelectionRange(valueLength, valueLength);
      cursorPositionedRef.current = true;
    }
  }, [isFocused, inputRef, value, formControlUse]);
  useEffect(() => {
    const shouldInsertUrlProtocol = hasUrlValidationRule && isFocused && !value;
    if (!shouldInsertUrlProtocol) {
      return;
    }
    const event = {
      target: {
        value: DEFAULT_PROTOCOL
      }
    };
    normalizeInputOnChange(event);
    // This is a special case of adding the url protocol for non-custom URL properties.
    // The validation rules load async, so the protocol won't be added in the handleFocus callback.
    // We want this effect to run only when shouldInsertUrlProtocol updates and the rules
    // are loaded before any input to insert the protocol. For any subsequent focus events,
    // this behavior is handled in handleFocus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUrlValidationRule]);
  const handleBlur = useCallback(event => {
    if (isUrlProperty && value === DEFAULT_PROTOCOL) {
      normalizeInputOnChange(Object.assign({}, event, {
        target: Object.assign({}, event.target, {
          value: ''
        })
      }));
    }
    normalizeInputOnBlur(event);
  }, [isUrlProperty, normalizeInputOnChange, normalizeInputOnBlur, value]);
  return /*#__PURE__*/_jsx(PropertyFormattedTooltip, {
    normalizationRules: normalizationRules,
    children: /*#__PURE__*/_jsx(ExpandableText, {
      buttonAlign: "left",
      hideButtonCaret: true,
      onCollapseButtonClick: () => trackExpandableTextCollapse({
        component: 'PropertyInputExpandableText',
        propertyName: property.name,
        objectTypeId
      }),
      onExpandButtonClick: () => trackExpandableTextExpand({
        component: 'PropertyInputExpandableText',
        propertyName: property.name,
        objectTypeId
      })
      // @ts-expect-error - borderRadius works here and fixes a visual bug, but is not yet added to the type
      ,
      borderRadius: 100,
      children: /*#__PURE__*/_jsx(ExpandingTextArea, Object.assign({}, getTransferrableProps(props), {
        value: value === null ? defaultValue || '' : value,
        inputRef: inputRef,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeyDown: normalizeInputOnKeyDown,
        shrink: true
      }))
    })
  });
};
export default PropertyInputExpandableText;